import { CommonModule } from "@angular/common";
import { Component, inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { BankAccount } from "@models/bank-account";
import { Wallet } from "@models/wallet";
import { TaskType } from "@models/task-request";
import { StatementFilters } from "@models/transaction";
import { TranslateModule } from "@ngx-translate/core";
import { isEmpty } from "lodash-es";
import { PaginatorState } from "primeng/paginator";
import { SkeletonModule } from "primeng/skeleton";
import { TableModule } from "primeng/table";
import { TabsModule } from "primeng/tabs";
import {
  finalize,
  firstValueFrom,
  Observable,
  shareReplay,
  take,
  tap,
} from "rxjs";
import { AccountStatementTableComponent } from "../../../app/pages/wallet-page/account-statement-table/account-statement-table.component";
import { CardBankAccountComponent } from "../../../app/pages/wallet-page/card-bank-account/card-bank-account.component";
import { EditBankAccountDialogComponent } from "../../../app/pages/wallet-page/edit-bank-account-dialog/edit-bank-account-dialog.component";
import { PendingChangesDialogComponent } from "../../../app/pages/wallet-page/pending-changes-dialog/pending-changes-dialog.component";
import { StatementFiltersComponent } from "../../../app/pages/wallet-page/statement-filters/statement-filters.component";
import { WithdrawDialogComponent } from "../../../app/pages/wallet-page/withdraw-dialog/withdraw-dialog.component";
import {
  AdminTasksService,
  WITHDRAWAL_UNAVAILABLE_MESSAGE,
} from "../../../app/services/admin-tasks.service";
import { SnackBarService } from "../../../app/services/snack-bar.service";
import { WalletService } from "../../../app/services/wallet.service";
import { DataCardComponent } from "../../../app/shared/components/cards/data-card/data-card.component";
import {
  ConfirmActionModalComponent,
  ConfirmDialogData,
} from "../../../app/shared/modals/confirm-action-modal/confirm-action-modal.component";
import { SectionHeaderComponent } from "../../../app/shared/section-header/section-header.component";
import { AppLayoutComponent } from "../app-layout/app-layout.component";
import { ButtonComponent } from "../button/button.component";
import { PageHeaderComponent } from "../page-header/page-header.component";

@Component({
  selector: "app-wallet",
  imports: [
    CommonModule,
    AppLayoutComponent,
    TableModule,
    TranslateModule,
    DataCardComponent,
    SectionHeaderComponent,
    CardBankAccountComponent,
    // LastWithdrawalsTableComponent,
    AccountStatementTableComponent,
    StatementFiltersComponent,
    TabsModule,
    SkeletonModule,
    PageHeaderComponent,
    ButtonComponent,
  ],
  templateUrl: "./wallet.component.html",
  styleUrl: "./wallet.component.scss",
})
export class WalletComponent {
  private readonly _dialog = inject(MatDialog);
  private readonly _tasksService = inject(AdminTasksService);
  private readonly _walletService = inject(WalletService);
  private readonly _snackBar = inject(SnackBarService);

  bankAccount$: Observable<null | BankAccount> = this._walletService
    .getBankAccount()
    .pipe(shareReplay());
  private readonly withdrawalTaskType = TaskType.WITHDRAWAL;
  withdrawals$ = this._tasksService.getTasks(
    null,
    undefined,
    null,
    this.withdrawalTaskType,
  );
  transactions$ = this._walletService.getTransactions();
  wallet$ = this._walletService.getWallet().pipe(shareReplay());
  deletingBankAccount = false;

  handleEditBankAccountClick(bankAccount: BankAccount | null) {
    const dialogRef = this._dialog.open(EditBankAccountDialogComponent, {
      width: "500px",
      data: { bankAccount: bankAccount },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.bankAccount$ = this._walletService
          .getBankAccount()
          .pipe(shareReplay());
      }
    });
  }

  withdrawableBalance(wallet: Wallet): number {
    const source = wallet.available_for_withdrawal ?? wallet.balance_released;
    return this.availableBalance(source);
  }

  handleRequestWithdrawClick(wallet: Wallet, bankAccount: BankAccount | null) {
    if (!bankAccount) {
      this.alertMissingBankAccount();
      return;
    }
    if (!isEmpty(bankAccount.pending_changes)) {
      this.alertPendingBankAccountChange();
      return;
    }
    const withdrawable = this.withdrawableBalance(wallet);
    if (withdrawable <= 0) {
      this.alertNoAvailableBalance(this.availableBalance(wallet.balance_released) > 0);
      return;
    }

    this.openWithdrawDialog(withdrawable);
  }

  private alertMissingBankAccount() {
    this._dialog
      .open(ConfirmActionModalComponent, {
        width: "500px",
        data: {
          modalTitle: "Cadastre uma conta bancária",
          modalContent:
            "Você ainda não possui uma <strong>conta bancária cadastrada</strong>. " +
            "Para realizar um saque é necessário cadastrar uma conta.",
          confirmMessage: "Cadastrar conta",
          cancelMessage: "Cancelar",
        } as ConfirmDialogData,
      })
      .afterClosed()
      .pipe(
        take(1),
        tap((confirmed) => {
          if (confirmed) {
            this.handleEditBankAccountClick(null);
          }
        }),
      )
      .subscribe();
  }

  private availableBalance(value: unknown): number {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return 0;
    return Math.round(amount * 100) / 100;
  }

  private alertNoAvailableBalance(reservedByPendingRequest = false) {
    this._snackBar.openSnackBar(
      WITHDRAWAL_UNAVAILABLE_MESSAGE,
      "Fechar",
      "error",
    );
    const modalContent = reservedByPendingRequest
      ? "O saldo liberado já está comprometido por <strong>solicitações de saque em análise</strong>. " +
        "Ele volta a ficar disponível se a solicitação for recusada."
      : "Você não possui <strong>saldo disponível para saque</strong>. " +
        "O valor em <strong>Saldo a liberar</strong> só pode ser sacado depois da compensação " +
        "(até 3 dias após o término do evento).";
    this._dialog.open(ConfirmActionModalComponent, {
      width: "500px",
      data: {
        modalTitle: WITHDRAWAL_UNAVAILABLE_MESSAGE,
        modalContent,
        confirmMessage: "Entendi",
        cancelMessage: "",
      } as ConfirmDialogData,
    });
  }

  // Já existe uma solicitação de conta bancária em andamento: bloqueia o saque e
  // novas solicitações até a administração aprovar ou recusar.
  private alertPendingBankAccountChange() {
    this._dialog.open(ConfirmActionModalComponent, {
      width: "500px",
      data: {
        modalTitle: "Solicitação bancária em andamento",
        modalContent:
          "Você já possui uma <strong>solicitação de conta bancária em andamento</strong>. " +
          "Não é possível sacar ou enviar uma nova solicitação até que ela seja " +
          "<strong>aprovada ou recusada</strong> pela administração.",
        confirmMessage: "Entendi",
        cancelMessage: "",
      } as ConfirmDialogData,
    });
  }

  private openWithdrawDialog(accountBalance: number) {
    const dialogRef = this._dialog.open(WithdrawDialogComponent, {
      width: "500px",
      data: {
        isAdvanceWithdraw: false,
        accountBalance,
      },
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (!res) return;

      this.wallet$ = this._walletService.getWallet().pipe(shareReplay());
      this.withdrawals$ = this._tasksService.getTasks(
        null,
        undefined,
        null,
        this.withdrawalTaskType,
      );
    });
  }

  handleDeleteBankAccountClick() {
    if (this.deletingBankAccount) return;

    this.deletingBankAccount = true;
    this._walletService
      .deleteBankAccount()
      .pipe(
        take(1),
        finalize(() => (this.deletingBankAccount = false)),
        tap(() => {
          this.bankAccount$ = this._walletService
            .getBankAccount()
            .pipe(shareReplay());
        }),
      )
      .subscribe();
  }

  handleLastWithdrawalsChange(pageEvent: PaginatorState) {
    this.withdrawals$ = this._tasksService.getTasks(
      pageEvent,
      undefined,
      null,
      this.withdrawalTaskType,
    );
  }

  private _currentFilters: StatementFilters = {};

  handleAccountStatementChange(pageEvent: PaginatorState) {
    this.transactions$ = this._walletService.getTransactions(
      pageEvent,
      this._currentFilters,
    );
  }

  handleAccountStatementFilterChange(filters: StatementFilters) {
    this._currentFilters = filters;
    this.transactions$ = this._walletService.getTransactions(
      undefined,
      filters,
    );
  }

  async handleViewPendingChangesClick() {
    const bankAccount = await firstValueFrom(this.bankAccount$);
    const pending = bankAccount?.pending_changes;
    if (isEmpty(pending)) return;

    this._dialog.open(PendingChangesDialogComponent, {
      width: "500px",
      data: { pendingChanges: pending },
    });
  }
}
